/**
 * Post Filter module.
 *
 * Turns any core/categories block marked with the "is-post-filter"
 * className (see lib/block-modifications/core-categories-post-filter.php)
 * into an AJAX taxonomy filter for its associated Query Loop block —
 * clicking a term link fetches the CURRENT page's own URL with the
 * taxonomy's query var appended (never the term's real archive URL, since
 * that's typically a different page/template that doesn't even contain
 * this same Query Loop) and swaps in the matching Query Loop's fresh
 * content, with no page load. The address bar only ever shows the current
 * path with a #term-slug fragment (never the real fetch URL, and never a
 * real navigation), which is what changing the hash alone does: it
 * updates window.location and is bookmarkable/shareable, but browsers
 * don't request the network for a fragment-only change.
 *
 * Works with any number of independent filter+query-loop pairs on a page,
 * and composes with the diakon/load-more block for free — both just read
 * and write ordinary URL query args on the (hidden) fetch URL, so a
 * filtered fetch's Load More button already points at the correctly
 * filtered next page.
 */

/* global DOMParser */

/**
 * Finds the Query Loop associated with a filter control: the nearest
 * following sibling that is (or contains) a Query Loop block, walking up
 * through ancestors if needed.
 *
 * @param {Element} filterEl The filter control element.
 */
function findQueryLoop(filterEl) {
	let node = filterEl;

	while (node) {
		let sibling = node.nextElementSibling;

		while (sibling) {
			if (sibling.classList.contains('wp-block-query')) {
				return sibling;
			}

			const nested = sibling.querySelector?.('.wp-block-query');
			if (nested) {
				return nested;
			}

			sibling = sibling.nextElementSibling;
		}

		node = node.parentElement;
	}

	return null;
}

/**
 * Builds a same-page URL with the taxonomy's query var set to slug (or
 * removed, if slug is empty) — this is what actually gets fetched, so the
 * SAME page/template (and its Query Loop) re-renders filtered, instead of
 * navigating to the term's own (likely unrelated) archive page.
 *
 * @param {string} queryVar Taxonomy query var, e.g. "category_name".
 * @param {string} slug     Term slug, or '' to clear the filter.
 */
function buildFilterUrl(queryVar, slug) {
	const url = new URL(window.location.href);
	url.hash = '';

	if (slug) {
		url.searchParams.set(queryVar, slug);
	} else {
		url.searchParams.delete(queryVar);
	}

	return url.toString();
}

/**
 * Notifies other modules (e.g. the Load More block) that new content was
 * injected into the DOM, so they can initialize anything inside it.
 *
 * @param {Element} container The container that received new content.
 */
function announceContentUpdated(container) {
	document.dispatchEvent(
		new CustomEvent('diakon:content-updated', { detail: { container } })
	);
}

async function applyFilter(fetchUrl, slug) {
	const response = await fetch(fetchUrl);

	if (!response.ok) {
		throw new Error(`HTTP ${response.status}`);
	}

	const doc = new DOMParser().parseFromString(
		await response.text(),
		'text/html'
	);

	const filters = document.querySelectorAll('.diakon-post-filter');
	const fetchedFilters = doc.querySelectorAll('.diakon-post-filter');

	filters.forEach((filterEl, index) => {
		const fetchedFilterEl = fetchedFilters[index];

		if (!fetchedFilterEl) {
			return;
		}

		const queryLoop = findQueryLoop(filterEl);
		const fetchedQueryLoop = findQueryLoop(fetchedFilterEl);

		if (queryLoop && fetchedQueryLoop) {
			queryLoop.innerHTML = fetchedQueryLoop.innerHTML;
			announceContentUpdated(queryLoop);
		}

		filterEl.innerHTML = fetchedFilterEl.innerHTML;
	});

	const newUrl =
		window.location.pathname +
		window.location.search +
		(slug ? `#${slug}` : '');
	window.history.pushState({ diakonPostFilterSlug: slug || '' }, '', newUrl);
}

function initPostFilter(filterEl) {
	const queryVar = filterEl.dataset.taxonomyQueryVar;

	filterEl.addEventListener('click', (event) => {
		const link = event.target.closest('a');

		if (!link) {
			return;
		}

		event.preventDefault();

		const slug = link.dataset.termSlug || '';

		applyFilter(buildFilterUrl(queryVar, slug), slug).catch(() => {
			window.location.href = link.href;
		});
	});
}

/**
 * If the page loads (or is navigated to via back/forward) with a
 * #term-slug already in the URL, finds the matching term link — in
 * whichever filter has it — and applies that filter automatically.
 * On a plain page load, an empty slug (no hash) needs no action — the page
 * is already rendered unfiltered. On popstate (back/forward) it does: the
 * user may be returning FROM a filtered state TO the unfiltered one, which
 * needs its own fetch to restore, found via the "All"/clear link's
 * data-term-slug="" attribute.
 *
 * @param {string}  slug         Term slug from the URL hash, '' for none.
 * @param {boolean} forceOnEmpty Whether an empty slug should still fetch (used for popstate).
 */
function applyFilterFromSlug(slug, forceOnEmpty = false) {
	if (!slug && !forceOnEmpty) {
		return;
	}

	const link = document.querySelector(
		`.diakon-post-filter a[data-term-slug="${slug}"]`
	);

	if (!link) {
		return;
	}

	const filterEl = link.closest('.diakon-post-filter');
	const queryVar = filterEl?.dataset.taxonomyQueryVar;

	if (queryVar) {
		applyFilter(buildFilterUrl(queryVar, slug), slug).catch(() => {});
	}
}

export function initPostFilters() {
	document.querySelectorAll('.diakon-post-filter').forEach(initPostFilter);

	applyFilterFromSlug(window.location.hash.slice(1));

	window.addEventListener('popstate', () => {
		applyFilterFromSlug(window.location.hash.slice(1), true);
	});
}
